import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ToastContext, useFetch } from '@reveni-io/reveni-ui'

import commonServices from '@/common/services/services'
import generalServices from '@/general/services/services'

import { getDefaultRestockLocation, getSavePreferencesPayload } from './helpers'

const useAccountPreferences = ({ storeId, userId }) => {
  const { data, loading: fetchingPreferences } = useFetch(commonServices.getUserPreferences, {
    doInitialRequest: storeId && userId,
    params: { storeId, userId },
  })
  const { fetchData: savePreferencesService, loading: savingPreferences } = useFetch(
    generalServices.saveUserPreferences,
    {
      doInitialRequest: false,
    }
  )
  const [mentionsNotifications, setMentionsNotifications] = useState(null)
  const [storeByDefault, setStoreByDefault] = useState('null')
  const [hasChanges, setHasChanges] = useState(false)
  const [storePreferences, setStorePreferences] = useState([])
  const [defaultRestockLocation, setDefaultRestockLocation] = useState(null)

  const { setToastInfo } = useContext(ToastContext)
  const { t } = useTranslation()

  useEffect(() => {
    if (data) {
      setHasChanges(false)
      setMentionsNotifications(data.mentions_notifications)
      setStoreByDefault(data.default_store_id || 'null')
      setStorePreferences(data.store_preferences || [])
      setDefaultRestockLocation(getDefaultRestockLocation(data.store_preferences, storeId))
    }
  }, [data, storeId])

  const savePreferences = async () => {
    const payload = getSavePreferencesPayload({
      mentionsNotifications,
      storeByDefault,
      storePreferences,
    })
    try {
      await savePreferencesService({ storeId, userId, preferences: payload })
      setHasChanges(false)
      setToastInfo({ show: true, message: t('Guardado correctamente'), type: 'success' })
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const revertChanges = () => {
    setMentionsNotifications(data.mentions_notifications)
    setStoreByDefault(data.default_store_id || 'null')
    setStorePreferences(data.store_preferences || [])
    setHasChanges(false)
  }

  return {
    accountPreferences: data,
    savePreferences,
    loading: fetchingPreferences || savingPreferences,
    hasChanges,
    revertChanges,
    setMentionsNotifications: value => {
      setMentionsNotifications(value)
      setHasChanges(true)
    },
    mentionsNotifications,
    storeByDefault,
    setStoreByDefault: store => {
      setStoreByDefault(store)
      setHasChanges(true)
    },
    storePreferences,
    setStorePreferences: locations => {
      setStorePreferences(locations)
      setHasChanges(true)
    },
    defaultRestockLocation,
  }
}

export default useAccountPreferences
